import { useTranslation } from 'react-i18next';
import { Button, ExerciseCard, TapChip, type TapChipAnswerState } from '@fluent/ui';
import type { TranslateTap as TranslateTapExercise } from '@fluent/types';
import type { MatchVerdict } from './matchAnswer.js';
import { playSfx } from './sounds.js';
import './TranslateTap.css';

export interface TranslateTapProps {
  exercise: TranslateTapExercise;
  /** Shuffled token pool — same array instance across renders for a given
   * exercise. Lifted into the parent so the submit handler has access. */
  pool: readonly string[];
  /** Indices into the pool that the user has tapped, in order. */
  selectedIndices: number[];
  onSelectedIndicesChange: (next: number[]) => void;
  locked: boolean;
  /** Set after the user submits — drives the per-word reveal on the answer row. */
  lastVerdict: MatchVerdict | null;
}

/**
 * TranslateTap — Georgian prompt on top, shuffled token pool below.
 * User taps tokens from the pool into the answer row; tapping an
 * answer chip returns it to the pool. Order matters at check time.
 *
 * Controlled: LessonPlayer owns both the shuffled pool and the
 * selected indices so the submit action can read the built sentence.
 */
export function TranslateTap({
  exercise,
  pool,
  selectedIndices,
  onSelectedIndicesChange,
  locked,
  lastVerdict,
}: TranslateTapProps) {
  const { t } = useTranslation();

  const isInAnswer = (i: number) => selectedIndices.includes(i);

  // Post-submit the answer row stops being a flat list and becomes the
  // correction: each chip says whether IT is the wrong one, and empty slots
  // mark the words that never made it in. Before that, everything is idle.
  const answerTokens = stripPunctuationTokens(exercise.answer_tokens);
  const chipStates = lastVerdict === null
    ? null
    : answerChipStates(selectedIndices.map((i) => pool[i]), answerTokens);
  const missingCount = chipStates === null
    ? 0
    : Math.max(0, answerTokens.length - selectedIndices.length);

  const addToken = (i: number) => {
    if (locked || isInAnswer(i)) return;
    playSfx('tap');
    onSelectedIndicesChange([...selectedIndices, i]);
  };

  const removeToken = (i: number) => {
    if (locked) return;
    playSfx('tap');
    onSelectedIndicesChange(selectedIndices.filter((x) => x !== i));
  };

  const clearAll = () => {
    if (locked) return;
    onSelectedIndicesChange([]);
  };

  return (
    <ExerciseCard
      kicker={t('lesson.translate_tap.kicker')}
      prompt={exercise.prompt}
      promptLang="ka"
    >
      <div className="fluent-ttap">
        <div
          className="fluent-ttap__answer"
          role="list"
          aria-label={t('lesson.translate_tap.answer_aria')}
          data-testid="translate-tap-answer"
        >
          {selectedIndices.length === 0 && (
            <span className="fluent-ttap__placeholder" aria-hidden="true">
              {t('lesson.translate_tap.answer_placeholder')}
            </span>
          )}
          {selectedIndices.map((idx, pos) => (
            <TapChip
              key={idx}
              lang="en"
              answerState={chipStates?.[pos]}
              onClick={() => removeToken(idx)}
              disabled={locked}
              data-testid={`tap-answer-${pool[idx]}`}
            >
              {pool[idx]}
            </TapChip>
          ))}
          {answerTokens.slice(answerTokens.length - missingCount).map((word, i) => (
            <TapChip key={`missing-${i}`} lang="en" hidden>
              {word}
            </TapChip>
          ))}
        </div>

        <div
          className="fluent-ttap__pool"
          role="list"
          aria-label={t('lesson.translate_tap.pool_aria')}
        >
          {pool.map((word, i) => (
            <TapChip
              key={i}
              lang="en"
              hidden={isInAnswer(i)}
              onClick={() => addToken(i)}
              disabled={locked}
              data-testid={`tap-pool-${word}`}
            >
              {word}
            </TapChip>
          ))}
        </div>

        <div className="fluent-ttap__controls">
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            disabled={locked || selectedIndices.length === 0}
          >
            {t('lesson.translate_tap.clear')}
          </Button>
        </div>
      </div>
    </ExerciseCard>
  );
}

/**
 * Per-chip verdict for the answer row, by position. A chip is `correct`
 * only when it is the right word AND in the right slot — order is the whole
 * point of the exercise, so "right word, wrong place" has to read as wrong.
 * Tokens tapped past the end of the answer are wrong too (extra words).
 */
export function answerChipStates(
  selectedTokens: readonly string[],
  answerTokens: readonly string[],
): TapChipAnswerState[] {
  return selectedTokens.map((tok, i) => (tok === answerTokens[i] ? 'correct' : 'wrong'));
}

/** Pure check: returns the verdict given the user's tokens. */
export function checkTranslateTap(
  selectedTokens: readonly string[],
  answerTokens: readonly string[],
): 'exact' | 'wrong' {
  if (selectedTokens.length !== answerTokens.length) return 'wrong';
  for (let i = 0; i < selectedTokens.length; i++) {
    if (selectedTokens[i] !== answerTokens[i]) return 'wrong';
  }
  return 'exact';
}

const TRAILING_PERIOD = /^\.+$/;

/**
 * Strip period-only tokens (".") from a token list. Tornike's call
 * 2026-05-18: the trailing period chip in sentence-builder exercises
 * is confusing and teaches nothing — but `?`, `,`, `!`, `;`, `:` stay
 * because they carry meaning learners should encounter.
 *
 * Tokens like "U.S." or "Mr." pass through unchanged (they contain
 * letters). Multi-period sequences like "..." also get stripped.
 */
export function stripPunctuationTokens(tokens: readonly string[]): string[] {
  return tokens.filter((t) => !TRAILING_PERIOD.test(t));
}
